/**
 * @fileoverview Archive (drop) and Restore Prospect mutation hook.
 * @description Drops a prospect with its mandatory reason (POST /sales/prospects/{id}/archive) or
 * restores a dropped one with an optional note (POST /sales/prospects/{id}/restore), both with the
 * prospect's ETag as If-Match, and refreshes every sales query once it succeeds. One hook serves
 * both because the dialogs differ only in the note's rules.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useIdempotentMutation } from '@/features/sales/hooks/useIdempotentMutation'
import { archiveProspect, restoreProspect } from '@/features/sales/services/prospects.service'

import type { ProspectArchiveResult } from '@/features/sales/types/prospect.types'

/** The input of one archive or restore command. */
export type ArchiveProspectInput =
  | { prospectId: string; etag: string; action: 'archive'; reasonNote: string }
  | { prospectId: string; etag: string; action: 'restore'; reasonNote: string | null }

/**
 * @description Drops or restores a prospect. The command's idempotency key outlives the dialog, so
 * a retry after an unknown outcome replays it.
 * @param {object} [options] - What follows a success.
 * @param {(result: ProspectArchiveResult, input: ArchiveProspectInput) => void} [options.onSuccess]
 * - Called after the change is saved, even when the dialog has unmounted since.
 * @returns {ReturnType<typeof useIdempotentMutation>} The mutation (mutate with an
 * ArchiveProspectInput).
 */
export function useArchiveProspect({
  onSuccess,
}: {
  onSuccess?: ((result: ProspectArchiveResult, input: ArchiveProspectInput) => void) | undefined
} = {}) {
  return useIdempotentMutation<ArchiveProspectInput, ProspectArchiveResult>({
    action: 'archive-prospect',
    /**
     * @description Scopes the key to the prospect and the change made, not the version it is sent against.
     * @param {ArchiveProspectInput} input - The change's input.
     * @returns {object} The prospect id, the action and the reason.
     */
    scopeOf: ({ prospectId, action, reasonNote }) => ({ prospectId, action, reasonNote }),
    /**
     * @description Archives or restores the prospect under the key it is given.
     * @param {ArchiveProspectInput} input - The prospect, its version, the action and the reason.
     * @param {string} key - The Idempotency-Key.
     * @returns {Promise<ProspectArchiveResult>} The result.
     */
    send: (input, key) =>
      input.action === 'archive'
        ? archiveProspect(input.prospectId, input.etag, input.reasonNote, key)
        : restoreProspect(input.prospectId, input.etag, input.reasonNote, key),
    onSuccess,
  })
}
